import apiClient from './api';

// ── Types ──

export type ShellJobTarget = 'all' | 'list' | 'filter' | 'group';
/**
 * `waiting`: the online pass is done; a kept-open job awaits offline devices.
 * `paused`: a wave rollout stopped between waves (failure limit, or "wait for me").
 */
export type ShellJobStatus = 'running' | 'waiting' | 'paused' | 'completed' | 'cancelled' | 'interrupted';
export type ShellJobItemStatus =
  | 'pending'
  | 'running'
  | 'ok'
  | 'exit_nonzero'
  | 'timeout'
  | 'failed'
  | 'offline'
  | 'not_enrolled'
  | 'cancelled'
  /** Run with a deadline: runs when its device comes online, until the deadline. */
  | 'waiting'
  /** The deadline passed before it was sent. */
  | 'expired'
  /** Send-only job: delivered, answer not collected. */
  | 'sent';

/**
 * The Devices-page filter, as the backend takes it for target "filter".
 * Same names / values as the /devices query params, arrays instead of CSV.
 */
export interface ShellJobDeviceFilter {
  status?: string;
  type?: string[];
  sub_type?: string[];
  model?: string[];
  os_version?: string[];
  agent_version?: string[];
  rom_version?: string[];
  is_internal?: boolean;
  last_seen?: string;
  search?: string;
}

export interface ShellJobCounts {
  total: number;
  pending: number;
  running: number;
  ok: number;
  exit_nonzero: number;
  timeout: number;
  failed: number;
  offline: number;
  not_enrolled: number;
  cancelled: number;
  waiting: number;
  expired: number;
  sent: number;
}

/** One wave's tally. `ok` includes sent; failed = exit_nonzero + timeout + failed; active = pending + running. */
export interface ShellJobWave {
  wave: number;
  total: number;
  ok: number;
  failed: number;
  active: number;
}

export interface ShellJob {
  id: string;
  tenant_id: string;
  command: string;
  /** What the run is for, as typed when it was created; empty when none. */
  description?: string;
  timeout_ms: number;
  concurrency: number;
  target: ShellJobTarget;
  parent_job_id?: string;
  /** Set when target is "filter". */
  target_filter?: ShellJobDeviceFilter;
  /** No-timeout runs (timeout_ms 0): how long a sent device holds its slot. */
  slot_release_ms?: number;
  /** Target "group": the software update group, and its name while it exists. */
  target_group_id?: string;
  target_group_name?: string;
  status: ShellJobStatus;
  /** Creator's email. */
  created_by: string;
  created_at: string;
  started_at?: string;
  finished_at?: string;
  /** The run's deadline: nothing is sent after it. */
  expires_at?: string;
  /** Cumulative rollout percentages, e.g. [5, 30, 100]; [100] = all at once. */
  wave_percents: number[];
  /** 1-based wave the rollout has reached. */
  current_wave: number;
  /** Pause when more than this % of a wave fails; 0 = never. */
  fail_threshold_pct: number;
  pause_between: boolean;
  send_only: boolean;
  /** Why a paused job stopped, e.g. "Wave 1 done · 40 ok". */
  paused_reason?: string;
  waves: ShellJobWave[];
  counts: ShellJobCounts;
  /** How many of this job's offline / not_enrolled items are online right now. */
  offline_now_online: number;
}

export interface ShellJobItem {
  id: string;
  device_id: string;
  /**
   * Device details as they were when the command was sent, or when the run
   * was created for devices never sent: saved on the item, not read live.
   * machine_id is the id the command went to; empty when not enrolled.
   */
  machine_id: string;
  device_number: string;
  serial_number: string;
  device_type: string;
  model: string;
  agent_version: string;
  os_version: string;
  rom_version: string;
  status: ShellJobItemStatus;
  exit_code?: number;
  /** stdout / stderr are capped at 4 KB server-side. */
  stdout?: string;
  stderr?: string;
  error?: string;
  started_at?: string;
  finished_at?: string;
  updated_at: string;
  /** 1-based wave, same numbering as job.waves / current_wave. */
  wave: number;
  /** The device's last heartbeat (RFC3339). */
  last_online?: string;
  /** Ran because its device came online while the job was kept open, not in the initial pass. */
  caught_up?: boolean;
}

export interface ShellJobTargetRequest {
  /** Only honoured for super admins; omitted → caller's tenant. */
  tenant_id?: string;
  target: ShellJobTarget;
  /** Machine IDs — target="list" only; anything else comes back as "not_machine_id". */
  devices?: string[];
  /** target="filter" only. */
  filter?: ShellJobDeviceFilter;
  /** target="group" only: a software update group id (needs config:read). */
  group_id?: string;
}

export interface ShellJobPreview {
  tenant_id: string;
  total: number;
  online: number;
  offline: number;
  not_enrolled: number;
  /** Identifiers that matched no device in the tenant. */
  unknown: string[];
  /** Per-line check of a pasted list, in paste order (target "list" only). */
  lines?: ShellJobCheckLine[];
  /** Targeted devices already in other active runs of the tenant. */
  overlap?: { devices: number; runs: number };
}

/** What one pasted identifier resolved to. */
export interface ShellJobCheckLine {
  input: string;
  result: 'matched' | 'not_found' | 'not_machine_id' | 'duplicate';
  matched_by?: 'machine_id' | 'id' | 'serial' | 'number';
  device_id?: string;
  machine_id?: string;
  device_number?: string;
  serial_number?: string;
  type?: string;
  model?: string;
  is_internal?: boolean;
  /** The status the device would start with: pending = online, will run. */
  status?: 'pending' | 'offline' | 'not_enrolled';
  /** The earlier line that already selected this device. */
  duplicate_of?: string;
}

export interface CreateShellJobRequest extends ShellJobTargetRequest {
  command: string;
  /** Optional, up to 500 characters. */
  description?: string;
  /** Absent = 30 s; 0 = no timeout (send and forget). */
  timeout_ms?: number;
  /** With timeout_ms 0: how long a sent device holds its slot (default 60000). */
  slot_release_ms?: number;
  concurrency?: number;
  /** The run's deadline (RFC3339): nothing is sent after it; offline devices run when back, until then. 1 minute to 30 days ahead. */
  expires_at?: string;
  /** Cumulative %, rising, ending at 100, max 5 entries; absent / [100] = all at once. */
  wave_percents?: number[];
  /** 0 = never pause on failures. */
  fail_threshold_pct?: number;
  pause_between?: boolean;
  /** Deliver the command without collecting answers. */
  send_only?: boolean;
}

/** Another active run of the same device. */
export interface ShellJobAlsoIn {
  id: string;
  command: string;
  status: ShellJobStatus;
}

export interface ShellJobItemsResponse {
  items: ShellJobItem[];
  /** Pass back as updated_after on the next poll to get only the delta. */
  server_time: string;
}

const shellJobService = {
  preview: (data: ShellJobTargetRequest) =>
    apiClient.post<ShellJobPreview>('/admin/shell-jobs/preview', data),

  create: (data: CreateShellJobRequest) =>
    apiClient.post<ShellJob>('/admin/shell-jobs', data),

  list: (limit = 20) =>
    apiClient
      .get<{ jobs: ShellJob[] }>('/admin/shell-jobs', { params: { limit } })
      .then((r) => r.jobs ?? []),

  get: (id: string) => apiClient.get<ShellJob>(`/admin/shell-jobs/${id}`),

  items: (id: string, updatedAfter?: string) =>
    apiClient.get<ShellJobItemsResponse>(`/admin/shell-jobs/${id}/items`, {
      params: updatedAfter ? { updated_after: updatedAfter } : undefined,
    }),

  cancel: (id: string) => apiClient.post<ShellJob>(`/admin/shell-jobs/${id}/cancel`),

  rerunOffline: (id: string) =>
    apiClient.post<ShellJob>(`/admin/shell-jobs/${id}/rerun-offline`),

  /** Paused → next wave. */
  resume: (id: string) => apiClient.post<ShellJob>(`/admin/shell-jobs/${id}/resume`),

  /** Paused: run the failed devices of the waves so far again. */
  retryFailed: (id: string) => apiClient.post<ShellJob>(`/admin/shell-jobs/${id}/retry-failed`),

  /** Other active runs of an item's device (max 5). */
  alsoIn: (jobId: string, itemId: string) =>
    apiClient
      .get<{ runs: ShellJobAlsoIn[] }>(`/admin/shell-jobs/${jobId}/items/${itemId}/also-in`)
      .then((r) => r.runs ?? []),
};

export default shellJobService;
